import React, { FC, useState, useEffect } from 'react';
import { Card, Typography, Table, Input, Button, Space, DatePicker, notification, Empty, Spin, Divider, Row, Col, message, Tag, Statistic, Alert, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined, CopyOutlined, DatabaseOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getAuthToken } from '@/utils/auth';
import { getEndpointScores, getTransactionById } from '@/shared/api';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTransactionsStore, TransactionData } from '@/store/transactions';
import { useTernaryDarkMode } from 'usehooks-ts';
import type { HTTPError } from 'ky';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const parseJsonIfString = (value: any) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return { raw: value };
  }
};

const hasPayloadValue = (value: any): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

// List responses omit content/details. Full records store payload in details, content, or payload.
const extractPayload = (tx: any): any => {
  if (!tx) return {};

  const details = Array.isArray(tx.details)
    ? tx.details
    : Array.isArray(tx.metadata?.details)
      ? tx.metadata.details
      : [];

  if (details.length > 0) {
    const payloads: Record<string, any> = {};
    details.forEach((detail: any, index: number) => {
      const name = detail?.name || detail.type || detail.stage || `Payload ${index + 1}`;
      const content = detail?.content ?? detail?.data;
      if (content != null) {
        payloads[name] = parseJsonIfString(content);
      }
    });
    if (Object.keys(payloads).length > 0) return payloads;
  }

  if (hasPayloadValue(tx.content)) return parseJsonIfString(tx.content);
  if (hasPayloadValue(tx.payload)) return tx.payload;
  return {};
};

// Define Transaction interface inline
interface Transaction {
  _id: string;
  timestamp: string;
  status: string;
  source: string;
  destination: string;
  payload: any;
}

interface DateFilterValue {
  preset: string;
  startDate: string | null;
  endDate: string | null;
  displayText: string;
}

interface DataInvestigatorProps {
  configId: string;
  dateFilter?: DateFilterValue;
}

const DataInvestigator: FC<DataInvestigatorProps> = ({ configId, dateFilter }) => {
  const { isDarkMode } = useTernaryDarkMode({ localStorageKey: "theme" });
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [loadingPayload, setLoadingPayload] = useState(false);
  const loadingPayloadTxIdRef = React.useRef<string | null>(null);
  const [endpointScoresCache, setEndpointScoresCache] = useState<Map<string, any>>(new Map());

  // Use the transactions store
  const { 
    fetchTransactions: fetchStoreTransactions, 
    transactions: storeTransactions, 
    isLoading: isStoreLoading 
  } = useTransactionsStore();

  // Update dateRange when dateFilter changes
  useEffect(() => {
    if (dateFilter?.startDate && dateFilter?.endDate) {
      console.log("DataInvestigator - Received date filter:", dateFilter);
      setDateRange([
        dayjs(dateFilter.startDate),
        dayjs(dateFilter.endDate)
      ]);
      
      // Fetch transactions from store with the date filter
      fetchStoreTransactions(
        configId, 
        1, 
        50, // Reduced page size for integration studio
        dateFilter.startDate, 
        dateFilter.endDate, 
        true // force refresh
      );
    }
  }, [dateFilter, configId, fetchStoreTransactions]);

  // Update local transactions state when store data changes
  useEffect(() => {
    if (storeTransactions[configId]?.data) {
      const storeData = storeTransactions[configId].data;
      console.log(`DataInvestigator - Updating from store:`, {
        configId,
        totalTransactions: storeData.length,
        sampleTransaction: storeData.length > 0 ? {
          transactionId: storeData[0].transactionId,
          status: storeData[0].status,
          startDate: storeData[0].startDate, 
          endDate: storeData[0].endDate,
          hasPayload: storeData[0].payload ? true : false,
          hasContent: storeData[0].content ? true : false
        } : 'No data',
        lastFetched: storeTransactions[configId].lastFetched ? 
          new Date(storeTransactions[configId].lastFetched).toISOString() : 'unknown',
        storeSize: Object.keys(storeTransactions).length,
        fetchParams: storeTransactions[configId].fetchParams
      });
      
      // List API omits content/details/payload for performance.
      // Payload is loaded on demand via getTransactionById when View Payload is clicked.
      const formattedTransactions = storeData.map(storeTx => ({
        _id: storeTx.transactionId || 'unknown',
        timestamp: storeTx.startDate || new Date().toISOString(),
        status: storeTx.status || 'unknown',
        source: storeTx.sourceStartpoint || 'unknown',
        destination: storeTx.destinationEndpoint || 'unknown',
        payload: extractPayload(storeTx)
      }));
      
      setTransactions(formattedTransactions);
      setLoading(false);
    } else {
      console.log(`DataInvestigator - No store data for configId: ${configId}`);
    }
  }, [storeTransactions, configId]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Build date parameters
      const effectiveDateRange = dateRange || 
        (dateFilter?.startDate && dateFilter?.endDate ? 
          [dayjs(dateFilter.startDate), dayjs(dateFilter.endDate)] : null);
      
      if (effectiveDateRange) {
        console.log("DataInvestigator - Using date range:", effectiveDateRange[0].toISOString(), "to", effectiveDateRange[1].toISOString());
        
        // Use the store to fetch transactions
        await fetchStoreTransactions(
          configId,
          1, // page
          50, // pageSize - reduced for integration studio
          effectiveDateRange[0].toISOString(),
          effectiveDateRange[1].toISOString(),
          true // force refresh
        );
      } else {
        console.log("DataInvestigator - No date range specified, using default");
        
        // Use the store to fetch transactions with default dates
        const endDate = dayjs();
        const startDate = dayjs().subtract(7, 'day');
        await fetchStoreTransactions(
          configId,
          1, // page
          50, // pageSize - reduced for integration studio
          startDate.toISOString(),
          endDate.toISOString(),
          true // force refresh
        );
      }
      
      // The store update will trigger the useEffect above to update our local state
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      notification.error({
        message: 'Failed to load transactions',
        description: 'Could not fetch transaction data. Please try again later.',
      });
      setLoading(false);
    }
  };
  
  // Fetch transactions when component mounts (only if we don't have a date filter)
  useEffect(() => {
    if (configId && (!dateFilter?.startDate || !dateFilter?.endDate)) {
      console.log("DataInvestigator - No date filter on mount, using default");
      fetchTransactions();
    }
  }, [configId]); // We removed dateFilter as a dependency since it's handled in the first useEffect
  
  const handleSearch = () => {
    fetchTransactions();
  };

  const handleReset = () => {
    setSearchText('');
    setDateRange(null);
    fetchTransactions();
  };
  
  const handleViewPayload = async (record: Transaction) => {
    const txId = record._id;
    loadingPayloadTxIdRef.current = txId;
    setSelectedTransaction(record);
    setLoadingPayload(true);

    try {
      const token = getAuthToken();
      if (!token) {
        message.error('Not authenticated');
        return;
      }

      const fullTransaction = await getTransactionById(token, configId, txId);
      if (loadingPayloadTxIdRef.current !== txId) return;

      const payload = extractPayload(fullTransaction);

      setSelectedTransaction({
        ...record,
        payload,
        timestamp: (fullTransaction as any)?.startDate || record.timestamp,
        status: (fullTransaction as any)?.status || record.status,
        source: (fullTransaction as any)?.sourceStartpoint || record.source,
        destination: (fullTransaction as any)?.destinationEndpoint || record.destination,
      });
    } catch (error) {
      if (loadingPayloadTxIdRef.current !== txId) return;
      console.error('Failed to fetch transaction payload:', error);
      message.error('Failed to load transaction payload');
    } finally {
      if (loadingPayloadTxIdRef.current === txId) {
        loadingPayloadTxIdRef.current = null;
        setLoadingPayload(false);
      }
    }
  };

  const handleCopyPayload = () => {
    if (selectedTransaction?.payload) {
      const payloadText = JSON.stringify(selectedTransaction.payload, null, 2);
      navigator.clipboard.writeText(payloadText).then(() => {
        message.success('Payload copied to clipboard!');
      }).catch((err) => {
        message.error('Failed to copy payload');
        console.error('Failed to copy:', err);
      });
    }
  };
  
  // Helper functions for endpoint scoring
  const getPerformanceGradeColor = (grade: string) => {
    switch (grade) {
      case 'excellent': return '#52c41a';
      case 'good': return '#1890ff';
      case 'fair': return '#faad14';
      case 'poor': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const renderEndpointScoreBadge = (score: any) => {
    if (!score) return null;
    const color = getPerformanceGradeColor(score.performanceGrade);
    return (
      <Tooltip title={
        <div>
          <div><strong>Score:</strong> {score.score}/100</div>
          <div><strong>Error Rate:</strong> {score.errorRate}%</div>
          <div><strong>Speed:</strong> {score.avgSpeed}ms/KB</div>
        </div>
      }>
        <span style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: color,
          color: 'white',
          borderRadius: '10px',
          padding: '2px 6px',
          fontSize: '10px',
          fontWeight: 600,
          marginLeft: 6
        }}>
          {score.score}
        </span>
      </Tooltip>
    );
  };

  // Fetch endpoint scores for visible transactions
  useEffect(() => {
    const fetchEndpointScores = async () => {
      if (!configId || transactions.length === 0) return;

      const token = getAuthToken();
      if (!token) return;

      const uniqueEndpoints = new Set<string>();
      transactions.slice(0, 20).forEach(t => {
        if (t.source) uniqueEndpoints.add(`source:${t.source}`);
        if (t.destination) uniqueEndpoints.add(`dest:${t.destination}`);
      });

      for (const endpoint of uniqueEndpoints) {
        if (endpointScoresCache.has(endpoint)) continue;

        try {
          const [type, url] = endpoint.split(':');
          const scores = await getEndpointScores(
            token,
            configId,
            type === 'source' ? url : undefined,
            type === 'dest' ? url : undefined
          );

          setEndpointScoresCache(prev => {
            const newCache = new Map(prev);
            if (type === 'source' && scores.sourceStartpoint) {
              newCache.set(endpoint, scores.sourceStartpoint);
            } else if (type === 'dest' && scores.destinationEndpoint) {
              newCache.set(endpoint, scores.destinationEndpoint);
            }
            return newCache;
          });
        } catch (error) {
          console.debug('Failed to fetch endpoint score:', error);
        }
      }
    };

    fetchEndpointScores();
  }, [transactions, configId]);

  // Calculate statistics
  const totalTransactions = transactions.length;
  const successfulTransactions = transactions.filter(t => t.status === 'success').length;
  const failedTransactions = transactions.filter(t => t.status === 'failure' || t.status === 'error').length;
  const pendingTransactions = transactions.filter(t => t.status === 'pending').length;

  const columns: ColumnsType<Transaction> = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (text: string) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#1890ff' }} />
          <span>{dayjs(text).format('MMM D, YYYY HH:mm:ss')}</span>
        </Space>
      ),
      sorter: (a: Transaction, b: Transaction) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text: string) => {
        const statusConfig = {
          success: { color: 'success', icon: <CheckCircleOutlined /> },
          failure: { color: 'error', icon: <CloseCircleOutlined /> },
          error: { color: 'error', icon: <CloseCircleOutlined /> },
          pending: { color: 'processing', icon: <ClockCircleOutlined /> },
        };
        const config = statusConfig[text as keyof typeof statusConfig] || { color: 'default' as const, icon: null };
        return <Tag color={config.color} icon={config.icon}>{text.toUpperCase()}</Tag>;
      },
      filters: [
        { text: 'Success', value: 'success' },
        { text: 'Failure', value: 'failure' },
        { text: 'Error', value: 'error' },
        { text: 'Pending', value: 'pending' },
      ],
      onFilter: (value, record) => record.status === value.toString(),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      ellipsis: true,
      render: (text: string, record: Transaction) => {
        if (!text) return '-';
        const cacheKey = `source:${text}`;
        const score = endpointScoresCache.get(cacheKey);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{text}</span>
            {score && renderEndpointScoreBadge(score)}
          </div>
        );
      },
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
      ellipsis: true,
      render: (text: string, record: Transaction) => {
        if (!text) return '-';
        const cacheKey = `dest:${text}`;
        const score = endpointScoresCache.get(cacheKey);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{text}</span>
            {score && renderEndpointScoreBadge(score)}
          </div>
        );
      },
    },
    {
      title: 'Transaction ID',
      dataIndex: '_id',
      key: '_id',
      ellipsis: true,
      render: (text: string) => (
        <Text code copyable style={{ fontSize: '12px' }}>{text.substring(0, 12)}...</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Transaction) => (
        <Button 
          type="primary"
          size="small"
          onClick={() => handleViewPayload(record)}
          icon={<SearchOutlined />}
        >
          View Payload
        </Button>
      ),
    },
  ];
  
  const isLoading = loading || isStoreLoading;
  
  return (
    <div className={`data-investigator ${isDarkMode ? 'dark' : 'light'}`}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={4}>
              <DatabaseOutlined /> Data Investigator
            </Title>
            <Text type="secondary">
              Explore and analyze transaction payloads for this integration
            </Text>
          </div>

          {/* Statistics Dashboard */}
          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Total Transactions" 
                  value={totalTransactions}
                  prefix={<InfoCircleOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Successful" 
                  value={successfulTransactions}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Failed" 
                  value={failedTransactions}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Pending" 
                  value={pendingTransactions}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Search and Filters */}
          <Card title={<Space><SearchOutlined /> Search & Filters</Space>} size="small">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Row gutter={16}>
                <Col span={12}>
                  <Input
                    placeholder="Search transactions by ID, source, or destination"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onPressEnter={handleSearch}
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Col>
                <Col span={12}>
                  <RangePicker 
                    onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])} 
                    value={dateRange}
                    style={{ width: '100%' }}
                    showTime
                    format="MMM D, YYYY HH:mm"
                  />
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Space>
                    <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                      Apply Filters
                    </Button>
                    <Button 
                      onClick={handleReset} 
                      icon={<ReloadOutlined />}
                    >
                      Reset Filters
                    </Button>
                  </Space>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Button 
                    type="default"
                    icon={<ReloadOutlined />}
                    onClick={fetchTransactions}
                    loading={isStoreLoading}
                  >
                    Refresh Data
                  </Button>
                </Col>
              </Row>
              
              {dateFilter?.startDate && dateFilter?.endDate && (
                <Alert
                  message="Active Date Filter"
                  description={`Showing data from ${dayjs(dateFilter.startDate).format('MMM D, YYYY HH:mm')} to ${dayjs(dateFilter.endDate).format('MMM D, YYYY HH:mm')}`}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  closable
                />
              )}
            </Space>
          </Card>
        
          {/* Transactions Table */}
          {isLoading ? (
            <Card>
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Spin size="large" tip="Loading transactions..." />
              </div>
            </Card>
          ) : transactions.length === 0 ? (
            <Card>
              <Empty 
                description="No transaction data available for the selected filters"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={fetchTransactions} icon={<ReloadOutlined />}>
                  Reload Transactions
                </Button>
              </Empty>
            </Card>
          ) : (
            <Card title={<Space><DatabaseOutlined /> Transactions ({totalTransactions})</Space>}>
              <Table 
                dataSource={transactions} 
                columns={columns} 
                rowKey="_id"
                pagination={{ 
                  pageSize: 10,
                  showTotal: (total, range) => 
                    `Showing ${range[0]}-${range[1]} of ${total} transactions`,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '25', '50', '100']
                }}
                size="middle"
                scroll={{ x: 'max-content' }}
                bordered
              />
            </Card>
          )}
          
          {/* Payload Viewer */}
          {selectedTransaction && (
            <Card 
              title={
                <Space>
                  <SearchOutlined />
                  <span>Transaction Payload</span>
                  <Tag color="blue">{selectedTransaction._id}</Tag>
                </Space>
              }
              extra={
                <Space>
                  <Button 
                    type="primary" 
                    icon={<CopyOutlined />}
                    onClick={handleCopyPayload}
                    size="small"
                    disabled={loadingPayload || !selectedTransaction.payload || Object.keys(selectedTransaction.payload).length === 0}
                  >
                    Copy Payload
                  </Button>
                  <Button size="small" onClick={() => setSelectedTransaction(null)}>
                    Close
                  </Button>
                </Space>
              }
            >
              {loadingPayload ? (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <Spin size="large" tip="Loading payload..." />
                </div>
              ) : selectedTransaction.payload && Object.keys(selectedTransaction.payload).length > 0 ? (
                <pre style={{ 
                  maxHeight: '500px', 
                  overflow: 'auto',
                  background: isDarkMode ? '#1f1f1f' : '#fafafa',
                  color: isDarkMode ? '#fff' : '#000',
                  padding: '16px',
                  borderRadius: '6px',
                  border: isDarkMode ? '1px solid #434343' : '1px solid #d9d9d9',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  {JSON.stringify(selectedTransaction.payload, null, 2)}
                </pre>
              ) : (
                <Empty 
                  description="No payload data available for this transaction"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
              
              <Divider />
              
              <Row gutter={16}>
                <Col span={8}>
                  <Space direction="vertical" size="small">
                    <Text type="secondary" strong>Timestamp:</Text>
                    <Text>{dayjs(selectedTransaction.timestamp).format('MMM D, YYYY HH:mm:ss')}</Text>
                  </Space>
                </Col>
                <Col span={8}>
                  <Space direction="vertical" size="small">
                    <Text type="secondary" strong>Status:</Text>
                    <Tag color={selectedTransaction.status === 'success' ? 'success' : 'error'}>
                      {selectedTransaction.status.toUpperCase()}
                    </Tag>
                  </Space>
                </Col>
                <Col span={8}>
                  <Space direction="vertical" size="small">
                    <Text type="secondary" strong>Source:</Text>
                    <Text ellipsis>{selectedTransaction.source}</Text>
                  </Space>
                </Col>
              </Row>
            </Card>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default DataInvestigator; 